import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Tag } from 'lucide-react';
import { arrayMove } from '@dnd-kit/sortable';
import type { OnChangeFn, PaginationState } from '@tanstack/react-table';
import type { PromotionPolicyToken } from '@sleed/bizcommerce-api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { GridChrome, type GridTab } from './grid-chrome';
import { GridTable } from './grid-table';
import { tokenToGridRow } from './to-grid-row';
import type { PromotionGridRow } from '~/lib/fixtures/demo-grid';
import { usePromotionsList } from '~/hooks/use-promotions-list';
import { usePromotionMutations } from '~/hooks/use-promotion-mutations';
import { getPromotionState } from '~/lib/promotion-status';

const TABS: GridTab[] = ['all', 'active', 'scheduled', 'expired', 'draft'];

function stateOf(row: PromotionGridRow) {
	return getPromotionState({
		isActive: row.isActive,
		activeFrom: row.activeFrom,
		activeTo: row.activeTo,
		code: row.code,
		actionCount: row.actionTypes.length,
		isVisible: row.isVisible,
		hideFromOffers: row.hideFromOffers
	});
}

function EmptyState({ onNew }: { onNew: () => void }) {
	const { t } = useTranslation();
	return (
		<div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-surface py-16 text-center">
			<Tag size={40} className="text-foreground-disabled" strokeWidth={1.25} />
			<h2 className="text-md font-semibold text-foreground">{t('promotion.grid.empty.title')}</h2>
			<p className="text-sm text-foreground-muted">{t('promotion.grid.empty.subtitle')}</p>
			<Button onClick={onNew}>{t('promotion.surface.grid.new')}</Button>
		</div>
	);
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
	const { t } = useTranslation();
	return (
		<div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-surface py-16 text-center">
			<AlertCircle size={40} className="text-danger-fg" strokeWidth={1.25} />
			<h2 className="text-md font-semibold text-foreground">{t('promotion.grid.error.title')}</h2>
			<p className="text-sm text-foreground-muted">{t('promotion.grid.error.subtitle')}</p>
			<Button variant="outline" onClick={onRetry}>
				{t('common.retry')}
			</Button>
		</div>
	);
}

function GridSkeleton() {
	return (
		<div className="flex flex-col gap-3">
			<Skeleton className="h-28 w-full rounded-lg" />
			<Skeleton className="h-96 w-full rounded-lg" />
		</div>
	);
}

/** Promotions grid wired to the live API (usePromotionsList + reorder/delete mutations). */
export function PromotionsGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [filtersOpen, setFiltersOpen] = useState(false);

	const { data, isLoading, isError, refetch } = usePromotionsList({ take: 1000 });
	const { reorder, remove } = usePromotionMutations();

	// Tokens kept (sorted by priority) for reorder; VM rows derived for display.
	const tokens = useMemo<PromotionPolicyToken[]>(
		() => [...(data?.items ?? [])].sort((a, b) => (a.Priority ?? 0) - (b.Priority ?? 0)),
		[data]
	);
	const rows = useMemo(() => tokens.map(tokenToGridRow), [tokens]);

	const tabParam = searchParams.get('tab');
	const tab: GridTab = TABS.includes(tabParam as GridTab) ? (tabParam as GridTab) : 'all';
	const search = searchParams.get('q') ?? '';
	const pageIndex = Math.max(0, Number(searchParams.get('page') ?? '1') - 1);
	const pageSize = Number(searchParams.get('pageSize') ?? '25');
	const focusedId = searchParams.get('focus') ? Number(searchParams.get('focus')) : undefined;

	const updateParams = (mutate: (next: URLSearchParams) => void) => {
		const next = new URLSearchParams(searchParams);
		mutate(next);
		setSearchParams(next, { replace: true });
	};

	const counts = useMemo(() => {
		const c = { total: rows.length, active: 0, scheduled: 0, expired: 0, draft: 0 };
		for (const r of rows) {
			const s = stateOf(r);
			if (s === 'active') c.active++;
			else if (s === 'scheduled') c.scheduled++;
			else if (s === 'expired') c.expired++;
			else if (s === 'draft') c.draft++;
		}
		return c;
	}, [rows]);

	const filtered = useMemo(() => {
		const q = search.toLowerCase();
		return rows.filter(r => {
			if (tab !== 'all' && stateOf(r) !== tab) return false;
			if (q && !`${r.name} ${r.code ?? ''}`.toLowerCase().includes(q)) return false;
			return true;
		});
	}, [rows, tab, search]);

	const pagination: PaginationState = { pageIndex, pageSize };
	const onPaginationChange: OnChangeFn<PaginationState> = updater => {
		const nextPg = typeof updater === 'function' ? updater(pagination) : updater;
		updateParams(p => {
			p.set('page', String(nextPg.pageIndex + 1));
			p.set('pageSize', String(nextPg.pageSize));
		});
	};

	const onReorder = (activeId: number, overId: number) => {
		const oldIndex = tokens.findIndex(tk => tk.id === activeId);
		const newIndex = tokens.findIndex(tk => tk.id === overId);
		if (oldIndex === -1 || newIndex === -1) return;
		reorder.mutate(arrayMove(tokens, oldIndex, newIndex)); // N× update; invalidates on success
	};

	const onRowAction = (id: number, action: 'edit' | 'duplicate' | 'view' | 'export' | 'delete') => {
		if (action === 'edit') navigate(`/promotions/${id}`);
		else if (action === 'delete') remove.mutate(id);
		// duplicate / view / export wired later in Phase I.
	};

	if (isLoading) return <GridSkeleton />;
	if (isError) return <ErrorState onRetry={() => void refetch()} />;
	if (rows.length === 0) return <EmptyState onNew={() => navigate('/promotions/new')} />;

	return (
		<div className="flex flex-col gap-4">
			<GridChrome
				counts={counts}
				search={search}
				onSearch={q =>
					updateParams(p => {
						if (q) p.set('q', q);
						else p.delete('q');
						p.delete('page');
					})
				}
				activeTab={tab}
				onTabChange={tb =>
					updateParams(p => {
						if (tb === 'all') p.delete('tab');
						else p.set('tab', tb);
						p.delete('page');
					})
				}
				onNew={() => navigate('/promotions/new')}
				onOpenFilters={() => setFiltersOpen(true)}
			/>

			{filtered.length === 0 ? (
				<EmptyState onNew={() => navigate('/promotions/new')} />
			) : (
				<GridTable
					rows={filtered}
					pagination={pagination}
					onPaginationChange={onPaginationChange}
					onReorder={onReorder}
					onRowClick={id => navigate(`/promotions/${id}`)}
					onRowAction={onRowAction}
					focusedId={focusedId}
				/>
			)}

			<Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
				<SheetContent>
					<SheetHeader>
						<SheetTitle>{t('common.filters')}</SheetTitle>
					</SheetHeader>
					<p className="p-4 text-sm text-foreground-muted">{t('promotion.grid.filtersComingSoon')}</p>
				</SheetContent>
			</Sheet>
		</div>
	);
}
